// Shared 底版生成 pipeline 配置契约 (admin 套系配置页).
//
// 字段名是 HARD 前/后端契约 — 后端读 `config.plate_pipeline.{generate,faceswap,
// restore,upscale}` (snake_case) 装配 ComfyUI 图 (有序 stage: 生成→换脸→修脸→超分)。
// 整个 plate_pipeline 缺省 → 走后端现有 scene 硬编码链 (零回归)。
// 见 openspec change `suite-plate-pipeline-config`, specs/suite-plate-pipeline。

export type PlateGenerateEngine = 'onething' | 'jimeng' | 'flux_kontext';

/** 底版换脸模式 (与用户交付换脸区分)。 */
export type PlateFaceSwapMode = 'none' | 'reactor' | 'pulid' | 'pulid_reactor';

export type PlateRestoreModel = 'none' | 'GFPGANv1.4' | 'codeformer';

/** 生成段: base dev + 可选套系 LoRA。 */
export interface PlateGenerateConfig {
  lora_name?: string;
  lora_weight?: number; // 默认 0.7
  lora_trigger?: string;
  engine?: PlateGenerateEngine; // 默认 onething
  steps?: number;
  guidance?: number;
}

/** 换脸段: engine + mode。 */
export interface PlateFaceSwapConfig {
  engine?: string; // 默认 onething
  mode?: PlateFaceSwapMode;
}

/** 修脸段: model + visibility。 */
export interface PlateRestoreConfig {
  model?: PlateRestoreModel;
  visibility?: number; // [0,1], 默认 0.65
}

/** 超分段: 开关 + model。 */
export interface PlateUpscaleConfig {
  enabled?: boolean;
  model?: string; // 默认 RealESRGAN_x4plus
}

/**
 * 套系底版生成 pipeline, 存于 `config.plate_pipeline`。
 * 整块缺省 → 后端走现状硬编码链; 逐字段缺省 → 后端平台默认 (三层解析)。
 */
export interface PlatePipelineConfig {
  generate?: PlateGenerateConfig;
  faceswap?: PlateFaceSwapConfig;
  restore?: PlateRestoreConfig;
  upscale?: PlateUpscaleConfig;
}

// --- 默认值 (与后端约定; 留空即后端缺省, 这些仅作 UI placeholder/回填参考) ---

export const DEFAULT_LORA_WEIGHT = 0.7;
export const DEFAULT_GENERATE_ENGINE: PlateGenerateEngine = 'onething';
export const DEFAULT_RESTORE_VISIBILITY = 0.65;
export const DEFAULT_UPSCALE_MODEL = 'RealESRGAN_x4plus';
export const DEFAULT_FACESWAP_ENGINE = 'onething';

// --- 下拉选项 ---

export const GENERATE_ENGINE_OPTIONS: Array<{
  value: PlateGenerateEngine;
  label: string;
  desc: string;
}> = [
  { value: 'onething', label: 'onething (自建 ComfyUI)', desc: '自有 GPU 跑 base dev + LoRA, 底版引擎主力。默认。' },
  { value: 'jimeng', label: '即梦 API', desc: '即梦文生图, 亚洲脸/中文友好, 不占自有 GPU。' },
  { value: 'flux_kontext', label: 'Flux Kontext', desc: 'Flux Kontext API 兜底。' },
];

export const FACESWAP_MODE_OPTIONS: Array<{
  value: PlateFaceSwapMode;
  label: string;
  desc: string;
}> = [
  { value: 'none', label: '不换脸 (none)', desc: '只生成 clean 底版, 与纯 txt2img 一致。最省最快。' },
  { value: 'reactor', label: 'ReActor 精确', desc: '末端 ReActor 精确换脸到底版, ~8s 近零 GPU 成本。正脸底版效果好。' },
  { value: 'pulid', label: 'PuLID 神似', desc: 'PuLID 身份注入扩散生成, 神似本人、能修大角度。跑扩散, 慢且贵。' },
  { value: 'pulid_reactor', label: 'PuLID + ReActor 叠加', desc: 'PuLID 重绘保 ID + 末端 ReActor 精修, 最像。最重最慢最贵。' },
];

export const RESTORE_MODEL_OPTIONS: Array<{
  value: PlateRestoreModel;
  label: string;
  desc: string;
}> = [
  { value: 'none', label: '不修脸 (none)', desc: '不做人脸修复, 最保留原貌但可能粗糙。' },
  { value: 'GFPGANv1.4', label: 'GFPGAN v1.4', desc: '通用人脸修复, 快。修复力强但偏磨皮。' },
  { value: 'codeformer', label: 'CodeFormer', desc: '通常比 GFPGAN 更自然、塑料感更轻。推荐优先。' },
];
